/**
 * Firebase Phone Auth implementation of OtpProvider.
 *
 * Web:     invisible reCAPTCHA via RecaptchaVerifier + signInWithPhoneNumber.
 * Native:  Firebase JS phone auth does not work inside the Capacitor WebView
 *          the same way (no reliable reCAPTCHA / SafetyNet bridge). This file
 *          throws a clear "use native provider" error on native so a native
 *          path (e.g. @capacitor-firebase/authentication, or a custom-token
 *          exchange from an SMS gateway) can be slotted in without touching UI.
 *          See README "OTP provider swap" for the native wiring TODO.
 */
import {
  RecaptchaVerifier,
  signInWithPhoneNumber,
  type ConfirmationResult,
  type UserCredential,
} from 'firebase/auth';
import { auth } from '../firebase';
import { isNative } from '../platform';
import type { OtpConfirmation, OtpProvider } from './types';

interface FirebaseConfirmation extends OtpConfirmation {
  readonly provider: 'firebase';
  readonly result: ConfirmationResult;
}

const RECAPTCHA_CONTAINER_ID = 'recaptcha-container';

let recaptcha: RecaptchaVerifier | null = null;

function ensureRecaptchaContainer(): HTMLElement {
  let el = document.getElementById(RECAPTCHA_CONTAINER_ID);
  if (!el) {
    el = document.createElement('div');
    el.id = RECAPTCHA_CONTAINER_ID;
    el.style.display = 'none';
    document.body.appendChild(el);
  }
  return el;
}

function getRecaptcha(): RecaptchaVerifier {
  if (recaptcha) return recaptcha;
  ensureRecaptchaContainer();
  recaptcha = new RecaptchaVerifier(auth, RECAPTCHA_CONTAINER_ID, {
    size: 'invisible',
  });
  return recaptcha;
}

export const firebaseOtpProvider: OtpProvider = {
  name: 'firebase',

  async sendOtp(mobileE164: string): Promise<OtpConfirmation> {
    if (isNative) {
      // Extension point: implement native phone auth here (see README).
      throw new Error(
        'Firebase web phone-auth is not supported on native. Wire a native OTP provider (see README > OTP provider swap).',
      );
    }
    const verifier = getRecaptcha();
    const result = await signInWithPhoneNumber(auth, mobileE164, verifier);
    const confirmation: FirebaseConfirmation = {
      destination: mobileE164,
      provider: 'firebase',
      result,
    };
    return confirmation;
  },

  async verifyOtp(confirmation: OtpConfirmation, code: string): Promise<UserCredential> {
    const fb = confirmation as FirebaseConfirmation;
    if (fb.provider !== 'firebase' || !fb.result) {
      throw new Error('Invalid confirmation handle for firebase provider.');
    }
    return fb.result.confirm(code);
  },

  reset(): void {
    if (recaptcha) {
      recaptcha.clear();
      recaptcha = null;
    }
  },
};
